import { Box } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import ZoomInImage from "components/image/ZoomInImage";
import { MstItemDetailWheelDetail } from "features/mst_wheel/types";
import { formatWheelRimDiamDisplay } from "utils";

interface IWheelItem extends MstItemDetailWheelDetail {
  selected: boolean;
  optionsPrice: number;
  onSelect: (id: number) => void;
  index: number;
  discountRate: string;
  setDiscountRate: (value: string) => void;
  onFocusDiscountRate: () => void;
  onBlurDiscountRate: () => void;
}

interface WheelItemDetailProps {
  type: "tire" | "wheel";
  optionsPrice: number;
  /** Wheels visible in the horizontal list (pre-filtered in useQuoteWheel). */
  wheelsData: MstItemDetailWheelDetail[];
  onSelectionChange?: (selectedIds: number[]) => void; // Add callback for selected IDs
  discountRate: { id: number; value: string }[];
  setDiscountRate: (id: number, value: string) => void;
  selectedWheels?: number[]; // Add selected wheels prop for initial state
  setDiscountFocusWheelId: (id: number | null) => void;
}

const ROW_HEIGHT = 32;

const styles = {
  wheelItem: {
    height: ROW_HEIGHT,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderBottom: "1px solid var(--offwhite-600)",
    fontSize: "14px",
    mx: "10px",
  },
} as const;

/**
 * WheelItem component displays detailed information for a single wheel item.
 * Features:
 * - Displays wheel image with zoom icon.
 * - Shows all wheel details in a column.
 * - Highlights if selected.
 * - Handles selection and image zoom events.
 */
const WheelItem = React.memo((props: IWheelItem) => {
  const {
    id,
    wheel_image_url_main,
    maker_name,
    brand_name,
    wheel_size,
    wheel_hub_diam,
    wheel_color_name,
    fixed_discount,
    selected,
    onSelect,
    index,
    optionsPrice,
    regular_price,
    discounted_price,
    final_price,
    discountRate,
    setDiscountRate,
    onFocusDiscountRate,
    onBlurDiscountRate,
  } = props;
  const [visibleZoomIn, setVisibleZoomIn] = useState(false);

  const isEvenColumn = index % 2 === 0;
  const isDisabled = regular_price <= 0;
  const bgColor = isDisabled
    ? "var(--silver-500)"
    : !isEvenColumn
      ? "var(--offwhite-200)"
      : "white";

  // Handle image zoom functionality
  const handleZoomImage = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setVisibleZoomIn(true);
  }, []);

  const handleChangeSpecialDiscountRate = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isDisabled) return;
      const digits = e.target.value.replace(/[^0-9]/g, "").slice(0, 2);
      if (digits === "") {
        setDiscountRate("");
        return;
      }
      const n = parseInt(digits, 10);
      if (n === 0) {
        setDiscountRate("");
        return;
      }
      setDiscountRate(String(n));
    },
    [isDisabled, setDiscountRate],
  );

  const handleSelectItem = useCallback(() => {
    if (regular_price <= 0) {
      return; // Prevent selection if regular_price is invalid
    }
    onSelect(id);
  }, [onSelect, id, regular_price]);

  return (
    <>
      <Box
        sx={{
          width: 260,
          minWidth: 233,
          flexShrink: 0,
          mb: 1,
          borderRadius: 0,
          display: "flex",
          flexDirection: "column",
          bgcolor: bgColor,
          opacity: isDisabled ? 0.7 : 1,
        }}
      >
        {/* Wheel image section */}
        <Box
          sx={{
            height: 187,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderBottom: "1px solid var(--offwhite-600)",
            border: selected ? "5px solid var(--primary-500)" : "5px solid transparent",
            margin: "6px 22px 16px 22px",
            position: "relative",
            cursor: isDisabled ? "not-allowed" : "pointer",
          }}
          onClick={handleSelectItem}
        >
          <img
            src={wheel_image_url_main || "/img/wheel/wheel.svg"}
            alt="Wheel"
            style={{
              width: "95%",
              height: "auto",
              maxHeight: "90%",
              display: "block",
            }}
          />
          {/* Magnifying glass icon */}
          <Box
            sx={{
              position: "absolute",
              bottom: 5,
              right: 5,
              borderRadius: "50%",
              cursor: "pointer",
              width: 24,
              height: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={handleZoomImage}
          >
            <Box
              component="span"
              sx={{
                fontSize: "16px",
                color: "var(--gray-550)",
              }}
            >
              <img src="/img/icons/zoom-in.svg" alt="zoom in icon" />
            </Box>
          </Box>
        </Box>
        {/* Wheel information section */}
        <Box sx={styles.wheelItem}>{maker_name}</Box>
        <Box sx={styles.wheelItem}>{brand_name}</Box>
        <Box sx={styles.wheelItem}>
          {wheel_size} {formatWheelRimDiamDisplay(wheel_hub_diam)}&Phi;{" "}
        </Box>
        <Box sx={styles.wheelItem}>{wheel_color_name}</Box>
        <Box sx={styles.wheelItem}>{regular_price.toLocaleString()}円</Box>
        <Box sx={styles.wheelItem}>
          <span>{fixed_discount}%</span> <span style={{ margin: "0 12px" }}> / </span>
          <input
            type="text"
            inputMode="numeric"
            maxLength={2}
            value={discountRate}
            onChange={handleChangeSpecialDiscountRate}
            onFocus={onFocusDiscountRate}
            onBlur={onBlurDiscountRate}
            disabled={isDisabled}
            placeholder=""
            style={{
              width: "39px",
              height: "23px",
              textAlign: "center",
              border:
                !Number.isNaN(Number(discountRate)) && Number(discountRate) > 100
                  ? "2px solid var(--error)"
                  : "1px solid var(--silver-500)",
              padding: "4px 2px",
              marginTop: "0",
              marginRight: "2px",
              fontSize: "14px",
            }}
          />
          <span>%</span>
        </Box>
        <Box sx={styles.wheelItem}>{discounted_price.toLocaleString()}円</Box>
        <Box sx={styles.wheelItem}>{optionsPrice.toLocaleString()}円</Box>
        <Box sx={{ ...styles.wheelItem, fontWeight: "bold", border: "none" }}>
          {final_price.toLocaleString()}円
        </Box>
      </Box>
      <ZoomInImage
        visibleModal={visibleZoomIn}
        imageUrl="/img/wheel/wheel.svg"
        alt="temp image"
        setVisibleModal={setVisibleZoomIn}
      />
    </>
  );
});

WheelItem.displayName = "WheelItem";

/**
 * WheelItemDetail component displays a horizontally scrollable list of wheel items,
 * each with detailed information and selection capability.
 *
 * Features:
 * - Fixed left column with headers and image placeholder.
 * - Scrollable area with multiple WheelItem components.
 * - For wheels: allows selection of only 1 item (replaces previous selection).
 * - For tires: allows selection of up to 4 items.
 * - Toggle selection when clicking on already selected items.
 * - Uses mock data for demonstration.
 * @param {object} props - The component props.
 * @param {string} props.type - The type of items ("tire" or "wheel").
 * @param {IWheelItem[]} props.wheelsData - The array of wheel item data.
 * @param {Function} [props.onSelectionChange] - Optional callback to receive selected item IDs.
 * @returns {Element} The rendered WheelItemDetail component.
 */
function WheelItemDetail({
  type,
  optionsPrice,
  wheelsData,
  onSelectionChange,
  discountRate,
  setDiscountRate,
  selectedWheels: initialSelectedWheels = [],
  setDiscountFocusWheelId,
}: WheelItemDetailProps): JSX.Element {
  const [selectedWheels, setSelectedWheels] = useState<number[]>(initialSelectedWheels);
  const isTire = useMemo(() => type === "tire", [type]);

  // Update local state when initialSelectedWheels changes (e.g., when navigating back)
  React.useEffect(() => {
    // Sync local state with prop whenever it changes
    setSelectedWheels(initialSelectedWheels);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialSelectedWheels)]);

  const handleSelectWheel = useCallback(
    (id: number) => {
      setSelectedWheels((prev) => {
        let newSelection: number[];

        if (prev.includes(id)) {
          // If already selected, remove it (toggle off)
          newSelection = prev.filter((wheelId) => wheelId !== id);
        } else {
          // For wheels (isTire = false), only allow 1 selection
          // For tires (isTire = true), allow up to 4 selections
          const maxSelection = isTire ? 4 : 1;

          if (prev.length < maxSelection) {
            newSelection = [...prev, id];
          } else if (!isTire) {
            // If at max selection limit, replace the first item for wheels
            newSelection = [id];
          } else {
            newSelection = prev;
          }
        }

        // Call the callback with the new selection
        onSelectionChange?.(newSelection);
        return newSelection;
      });
    },
    [onSelectionChange, isTire],
  );

  const discountRateMap = useMemo(
    () => new Map(discountRate.map((dr) => [dr.id, dr.value])),
    [discountRate],
  );

  const findDiscountRate = useCallback(
    (id: number) => discountRateMap.get(id) ?? "",
    [discountRateMap],
  );

  const handleBlurDiscountRate = useCallback(
    () => setDiscountFocusWheelId(null),
    [setDiscountFocusWheelId],
  );

  // Only memoize large static data to avoid recreating each render
  const headers = useMemo(
    () => [
      { id: "maker_name", text: "ホイールメーカー" },
      { id: "brand_name", text: "ホイールブランド" },
      { id: "wheel_size", text: "ホイールサイズ/ハブ径" },
      { id: "wheel_color_name", text: "カラー" },
      { id: "regular_price", text: "ホイール通常4本売価" },
      { id: "fixed_discount", text: "固定割引率/特別割引率" },
      { id: "main_purchase_cost", text: "割引後4本価格" },
      { id: "optionTotal", text: "オプション合計" },
      { id: "list_price", text: "ホイール4本合計" },
    ],
    [],
  );

  return (
    <Box
      sx={{
        px: { sm: 2, md: 3, xl: 5 },
        pb: 3,
        display: "flex",
        gap: 4,
        borderRadius: 0,
        overflow: "hidden",
      }}
    >
      {/* Fixed left column with titles */}
      <Box
        sx={{
          width: 190,
          flexShrink: 0,
        }}
      >
        {/* Header without title (image section) */}
        <Box
          sx={{
            height: 219,
            fontSize: { xs: 12 },
            display: "flex",
            alignItems: "center",
          }}
        >
          ※商品画像をタップすると <br />
          商品を選択できます。
          <br />
          もう一度タップすると <br />
          選択解除となります
          <br />
        </Box>

        {/* Title headers */}
        {headers.map((header) => (
          <Box
            key={header.id}
            sx={{
              height: ROW_HEIGHT,
              px: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "start",
              fontWeight: "bold",
              fontSize: "14px",
              borderBottom: header.id !== "list_price" ? "1.5px solid var(--offwhite-600)" : "none",
              borderRight: "1px solid var(--offwhite-600)",
              bgcolor: "var(--primary-500)",
              textAlign: "center",
            }}
          >
            {header.text}
          </Box>
        ))}
      </Box>

      {/* Horizontal scrollable area with wheel list */}
      <Box
        sx={{
          display: "flex",
          overflowX: "auto",
          flexGrow: 1,
          "&::-webkit-scrollbar": {
            height: "10px",
          },
          "&::-webkit-scrollbar-track": {
            border: "1px solid #bcbcbc",
            background: "transparent",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#bcbcbc",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "var(--gray-250)",
          },
        }}
      >
        {wheelsData.map((wheel, index) => (
            <WheelItem
              // eslint-disable-next-line react/no-array-index-key
              key={`${wheel.id}_${index}`}
              {...wheel}
              selected={selectedWheels.includes(wheel.id)}
              onSelect={handleSelectWheel}
              index={index}
              optionsPrice={optionsPrice}
              discountRate={findDiscountRate(wheel.id)}
              setDiscountRate={(value) => setDiscountRate(wheel.id, value)}
              onFocusDiscountRate={() => setDiscountFocusWheelId(wheel.id)}
              onBlurDiscountRate={handleBlurDiscountRate}
            />
          ))}
      </Box>
    </Box>
  );
}

export default WheelItemDetail;
